import {Component, EventEmitter, Input, OnInit, Output, OnDestroy, ViewChild, ElementRef} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {Observable} from "rxjs/Observable";
import {
    FORM_DIRECTIVES,
    FORM_PROVIDERS,
    REACTIVE_FORM_DIRECTIVES,
    FormControl,
    FormGroup,
    Validators
} from "@angular/forms";
import {AlertComponent, MODAL_DIRECTIVES, BS_VIEW_PROVIDERS, TYPEAHEAD_DIRECTIVES} from "ng2-bootstrap/ng2-bootstrap";
import {BankService} from "../../service/bank.service";
import {Bank} from "../../domain/bank";
import {DEFAULT_ERROR_MESSAGES, EMAIL_VALIDATOR, FILE_MAX_SIZE} from "../../validators/validators";
import {Contact} from "../../domain/contact";
import {City} from "../../domain/city";
import {State} from "../../domain/state";
import {CitiesService} from "../../service/cities.service";
import {Address} from "../../domain/address";
import {FONTS_LIST, BLACK_COLOR, SERVILEASING_COLOR} from "../../common/common";
import {FileUploader, FILE_UPLOAD_DIRECTIVES} from "ng2-file-upload";
import {LookAndFeelBank} from "../../domain/lookAndFeelBank";

/**
 *.
 * Created by jlado on 19/8/16.
 */
@Component({
    selector: 'bank-editor',
    template: require('./bank.editor.component.html'),
    directives: [
        FORM_DIRECTIVES,
        REACTIVE_FORM_DIRECTIVES,
        AlertComponent,
        TYPEAHEAD_DIRECTIVES,
        MODAL_DIRECTIVES,
        FILE_UPLOAD_DIRECTIVES
    ],
    providers: [
        FORM_PROVIDERS,
        BS_VIEW_PROVIDERS
    ],
})
export class BankEditorComponent implements OnInit, OnDestroy {

    @Input() public bank:Bank;
    @Output() private close = new EventEmitter();

    public fonts:any;
    private defaultColorFont:any;
    private defaultColorBackground:any;
    private defaultFont:any;

    public errorMessages:any;
    public errorMessage:string;
    public successMessage:string;

    private sub:any;
    public form:FormGroup;

    public cities:City[];
    public states:State[];

    public newContact:Contact;
    public bankId:string;

    public createMode:boolean;
    public loading:boolean;

    public bankStateSelectedId:string;
    public bankCitySelectedId:string;

    public previewToken:string;
    public uploaderLogo:FileUploader;
    public hasLogoDropZoneOver:boolean;

    @ViewChild('imgNotFound') imgNotFound:ElementRef;
    @ViewChild('bankImageCreation') bankImageCreation:ElementRef;
    @ViewChild('bankImage') bankImage:ElementRef;

    constructor(private bankService:BankService,
                private citiesService:CitiesService,
                private route:ActivatedRoute) {
        this.init();
    }

    private init():void {
        this.fonts = FONTS_LIST;
        this.defaultColorFont = BLACK_COLOR;
        this.defaultColorBackground = SERVILEASING_COLOR;
        this.defaultFont = FONTS_LIST[0].name;

        this.errorMessages = DEFAULT_ERROR_MESSAGES;

        this.bank = new Bank();
        this.newContact = new Contact();

        this.loading = false;

        this.uploaderLogo = new FileUploader({
            maxFileSize: FILE_MAX_SIZE,
            /*allowedMimeType: ['image/png', 'image/gif', 'image/jpeg']*/
        });
        this.hasLogoDropZoneOver = false;
    }

    public ngOnInit() {
        this.createFormGroup();

        this.sub = this.route.params.subscribe(params => {
            let bankId = params['bankId'];
            if (bankId) {
                this.bankId = bankId;
                this.createMode = false;
            } else {
                this.bankId = null;
                this.createMode = true;
            }
            this.getData();
        });
    }

    private createFormGroup():void {
        let group:any = {};
        group['name'] = new FormControl(this.bank.name, Validators.required);
        group['phone'] = new FormControl(this.bank.phone);
        group['street'] = new FormControl(this.bank.address.street);
        group['addressNumber'] = new FormControl(this.bank.address.number);
        group['state'] = new FormControl(this.bank.address.city.state.name);
        group['city'] = new FormControl(this.bank.address.city.name);
        group['floor'] = new FormControl(this.bank.address.floor);
        group['department'] = new FormControl(this.bank.address.department);
        group['zipCode'] = new FormControl(this.bank.address.zipCode);

        group['newContactName'] = new FormControl(this.newContact.name);
        group['newContactPhone'] = new FormControl(this.newContact.phone);
        group['newContactEmail'] = new FormControl(this.newContact.email, [EMAIL_VALIDATOR]);

        group['font'] = new FormControl(this.defaultFont);
        group['fontColor'] = new FormControl(this.defaultColorFont);
        group['backgroundColor'] = new FormControl(this.defaultColorBackground);

        this.form = new FormGroup(group);
    }

    private getData():void {
        this.loading = true;
        let forkTasks:Observable<any>[] = [];
        forkTasks.push(this.citiesService.getStates());
        if (this.bankId) {
            forkTasks.push(this.bankService.getBank(this.bankId));
        }
        Observable.forkJoin(forkTasks).subscribe(
            this.setData.bind(this),
            () => {
                this.bindError('Ocurrió un error al obtener los datos del banco.');
            });
    }

    private createBank():void {
        this.bank = new Bank();
        this.initLookAndFeelBank();

        this.bankStateSelectedId = null;
        this.bankCitySelectedId = null;

        this.bankService.generateBankTempId()
            .subscribe(
                (response)=> {
                    this.bank.tempId = response.value;
                    this.updateUploaderUrl();
                },
                () => {
                    this.bindError('Ocurrió un error al crear template del banco.');
                });
    }

    private setData(data:any[]):void {
        this.loading = false;
        this.states = data[0];

        if (this.bankId) {
            this.bank = data[data.length - 1];
            this.bank.tempId = this.bank.id;
            if (!this.bank.address) {
                this.bank.address = new Address(null);
            } else if (this.bank.address.city.id != null && this.bank.address.city.state.id != null) {
                this.bankStateSelectedId = this.bank.address.city.state.id;
                this.bankCitySelectedId = this.bank.address.city.id;
                this.loadCities(this.bank.address.city.state);
            }
            if (!this.bank.lookAndFeelData) {
                this.bank.lookAndFeelData = new LookAndFeelBank(null);
                this.initLookAndFeelBank();
            }
            this.updateUploaderUrl();
        } else {
            this.createBank();
        }
        this.addContactsControls();
    }

    private updateUploaderUrl():void {
        let fileOwnerId = this.bank.tempId;
        this.uploaderLogo = new FileUploader({
            url: 'bank/' + fileOwnerId + '/upload',
            autoUpload: true,
            allowedMimeType: ['image/png', 'image/gif', 'image/jpeg'],
            maxFileSize: FILE_MAX_SIZE
        });
    }

    private clearMessages():void {
        this.successMessage = null;
        this.errorMessage = null;
    }

    public save():void {
        this.clearMessages();
        this.loading = true;

        /*Elimnar condición para carga de contactos múltiple*/
        if (this.createMode) {
            this.addNewContactToBank();
        }

        this.bankService.save(this.bank).subscribe(
            (saved:Bank) => {
                this.loading = false;
                this.bank = saved;
                this.successMessage = 'El banco se guardó con éxito';
                this.updateViewAfterSave();
            },
            () => {
                this.bindError('Ocurrió un error al guardar los datos del banco.');
            });
    }

    public checkBeforeSave():boolean {
        if (this.form.valid && !this.loading) {
            if (this.bank && this.bank.address && this.bank.address.city.id) {
                return this.bank.address.city.state.id == null;
            } else if (this.bank && this.bank.address && this.bank.address.city.state.id) {
                return this.bank.address.city.id == null;
            }
            return false;
        }
        return true;
    }

    private updateViewAfterSave():void {
        this.uploaderLogo.clearQueue();
        if (this.createMode) {
            this.createBank();
        } else {
            this.bank.tempId = this.bank.id;
        }
    }

    public goBack(event:any):void {
        event.preventDefault();
        this.close.emit(this.bank);
        window.history.back();
    }

    private loadCities(state:State):void {
        this.loading = true;
        this.citiesService.getCities(state.id)
            .subscribe(
                (data:City[]) => {
                    this.loading = false;
                    this.cities = data;
                },
                () => {
                    this.bindError('Ocurrió un error al obtener las ciudades.');
                });
    }

    private clearCity(state:State):void {
        this.bank.address.city = new City(null);
        this.bank.address.city.state = state;
    }

    private addContactsControls():void {
        let form = this.form;
        this.bank.contacts.forEach(function (contact, i) {
            form.controls['contactName' + i] = new FormControl(contact.name);
            form.controls['contactPhone' + i] = new FormControl(contact.phone);
            form.controls['contactEmail' + i] = new FormControl(contact.email, [EMAIL_VALIDATOR]);
        });
    }

    private initLookAndFeelBank():void {
        this.bank.lookAndFeelData.backgroundColor = this.defaultColorBackground;
        this.bank.lookAndFeelData.fontColor = this.defaultColorFont;
        this.bank.lookAndFeelData.font = this.defaultFont;
    }

    private addNewContactToBank():void {
        this.bank.contacts.push((JSON.parse(JSON.stringify(this.newContact))));
        this.newContact = new Contact();
        /*Elimnar para carga de contactos múltiple*/
        this.addContactsControls();
    }

    public fileOverLogo(e:any):void {
        this.hasLogoDropZoneOver = e;
    }

    public removeLogo():void {
        this.bank.logo = null;
    }

    public removeLogoFromQueue(logo:any):void {
        this.bankService.deleteTempBankDocument(this.bank.tempId)
            .subscribe(
                (result)=> logo.remove(),
                () => {
                    this.bindError('Ocurrió un error al eliminar el logo de la cola.');
                });
    }

    public previewBankFile():void {
        var date = new Date();
        this.previewToken = date.getTime().toString();
    }

    public onStateSelected(newValue:any):void {
        let result = this.states.filter(function (obj:State) {
            return obj.id == newValue;
        });
        if (result.length > 0) {
            this.loadCities(result[0]);
            this.clearCity(result[0]);
        }
    }

    public onCitySelected(newValue:any):void {
        let result = this.cities.filter(function (obj:City) {
            return obj.id == newValue;
        });
        if (result.length > 0) {
            this.bank.address.city = result[0];
        }
    }

    public imageNotFound():void {
        if(this.createMode) {
            this.bankImageCreation.nativeElement.setAttribute('src', this.imgNotFound.nativeElement.getAttribute('src'));
            this.bankImageCreation.nativeElement.setAttribute('height', 150);
            this.bankImageCreation.nativeElement.setAttribute('width', 150);
        } else {
            this.bankImage.nativeElement.setAttribute('src', this.imgNotFound.nativeElement.getAttribute('src'));
            this.bankImage.nativeElement.setAttribute('height', 150);
            this.bankImage.nativeElement.setAttribute('width', 150);
        }
    }

    public ngOnDestroy() {
        this.sub.unsubscribe();
    }

    private bindError(messageError:string):void {
        this.loading = false;
        if (this.errorMessage == null) {
            this.errorMessage = messageError;
        } else {
            this.errorMessage = this.errorMessage.toString() + ' ' + messageError;
        }
    }


    /* Descomentar si se necesitan inputs (typeahead) y reemplazarlos por los actuales
     public onStateSelected(state:any) {
     this.loadCities(state.item);
     this.clearCity(state.item);
     }

     public onCitySelected(city:any) {
     this.bank.address.city = city.item;
     }*/

    /* Descomentar si se necesitan carga de contactos múltiples para 1 banco
     public addNewContact(event:any) {
     event.preventDefault();
     this.addNewContactToBank();
     this.addContactsControls();
     }

     public deleteContact(event:any, index:number) {
     event.preventDefault();
     this.bank.contacts.splice(index, 1);
     }*/
}


/** WEBPACK FOOTER **
 ** ./src/app/config/banks/bank.editor.component.ts
 **/